/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {AccessToken, OktaAuth, OktaAuthOptions, UserClaims} from '@okta/okta-auth-js';
import isEmpty from 'lodash.isempty';
import isNil from 'lodash.isnil';
import {ApplicationKeys} from '@ion/applications/src/types/application';
import {getLocalStorage, setLocalStorage} from '@ion/core/src/capabilities/storage';
import {get} from '@ion/core/src/environment/environment';
import {error} from '@ion/core/src/logger/logger';
import {
  authenticate,
  currentUser,
  getOpenIdUserInfo,
  logout,
  queryOpenIdConfigurationForDomain
} from '@ion/core/src/services/Connect';
import {client, url} from '@ion/core/src/services/ConnectUtils';
import {
  getSessionToken,
  getSessionTokenExpiration
} from '@ion/core/src/services/CTCAuthToken';
import {
  authenticate as sdAuthenticate
} from '@ion/core/src/services/SDConnect';
import {GenericObject, User} from '@ion/core/src/types/entities';
import {Service} from '@ion/core/src/types/services';
import {decode, encode} from '@ion/core/src/utils/Cipher';
import {isSame} from '@ion/core/src/utils/DateUtil';
import {safeJSONParse} from '@ion/core/src/utils/JSONUtils';
import {setAuthToken as setGraphQLAuthToken} from '@ion/services/src/graphql/GraphQLConfig';
import {currentUserQuery} from '@ion/services/src/graphql/QueryAdapter';

/**
 * Provides an enumeration of all OIDC Authentication life-cycle states.
 */
export enum OIDCAuthState {
  OIDC_AUTH_DEFAULT = 'OIDC_AUTH_DEFAULT', //Login screen with username
  OIDC_DISCOVERY_START = 'OIDC_DISCOVERY_START', //Enter Username --> Click Next
  OIDC_DISCOVERY_SUCCESS = 'OIDC_DISCOVERY_SUCCESS', //idpUser = true --> Redirect
  OIDC_DISCOVERY_FAILURE = 'OIDC_DISCOVERY_FAILURE', //idpUser = false, Login screen with Password
  LOGIN_START = 'LOGIN_START', //OIDC_DISCOVERY_SUCCESS ? oktaLogin && parseToken : ctcLogin
  LOGIN_SUCCESS = 'LOGIN_SUCCESS', //parseToken successful || ctcLogin successful
  LOGIN_FAILURE = 'LOGIN_FAILURE', //parseToken fail || ctcLogin fail
  PREAUTH_FAILURE = 'PREAUTH_FAILURE' //preuath fail
};

/**
 * Provides an enumeration of various keys related to Login state in persistent storage.
 */
enum LOGIN_PERSISTENCE {
  LOGIN_SESSION = 'session',
  USER_NAME_VERIFICATION_KEY = 'usernameVerification',
  LOGIN_CREDENTIALS = 'login',
  OIDC_USERNAME = 'oidcUsename'
};

/**
 * Provides an enumeration of HTTP error codes which is used to determine sso strict mode.
 */
enum SSO_HTTP_ERROR {
  HTTP_NOT_FOUND = 404,
  HTTP_METHOD_NOT_ALLOWED = 405
}

/**
 * Interface type for SSO discovery response.
 */
interface SSOConfigResponse {
  config: OktaAuthOptions;
  isSso: boolean;
  error: boolean;
};

/**
 * The `AuthenticationManager` provides the single source of access for authentication
 * onto the CTC platform.
 *
 * ```typescript
 *
 *   // add example once implemented ...
 *
 * ```
 *
 */
class AuthenticationManager {
  private static _singleInstance: AuthenticationManager = null;
  private _authState: OIDCAuthState;
  private _oidcUsername: string;
  private _hasIdProvider: boolean;
  private _callbacks: Function[];
  private _isServiceError: boolean;
  private _isInvalid: boolean;
  private _persistCredentials: boolean;
  private _persisted: GenericObject;
  private _emptyCredentials: GenericObject;
  private _user: GenericObject;
  private _authClient: OktaAuth;
  private _oidcBaseUrl: string;

  public constructor() {
    this._authState = OIDCAuthState.OIDC_AUTH_DEFAULT;
    this._oidcUsername = '';
    this._hasIdProvider = false;
    this._isServiceError = false;
    this._isInvalid = false;
    this._callbacks = [];
    this._emptyCredentials = {
      defaultAccountId : null,
      rememberMe       : false,
      credentials      : {
        username : '',
        password : ''
      }
    };
    this._persisted = this.getPersisted();
    this._persistCredentials = this._persisted.rememberMe;
    this._user = this._persisted.credentials;
    this._authClient = null;
    this._oidcBaseUrl = null;
  }

  /**
   *
   * Provides a utility to check if OIDC feature is enabled
   *
   * ```typescript
   *
   *  import authMgr from '@ion/applications/src/authentication/AuthenticationManager';
   *
   *  const oidcEnabled = authMgr.getIsOIDCEnabled();
   *
   * ```
   *
   */
  public getIsOIDCEnabled(): boolean {
    return get('oidcEnabled');
  }

  /**
   *
   * Provides a utility to get OIDC base url
   *
   * ```typescript
   *
   *  import authMgr from '@ion/applications/src/authentication/AuthenticationManager';
   *
   *  const oidcUrl = authMgr.oidcBaseUrl;
   *
   * ```
   *
   */
  public get oidcBaseUrl(): string {
    if (!this._oidcBaseUrl) {
      this._oidcBaseUrl = get('oidcBaseUrl') || get('ctcURL');
    }
    return this._oidcBaseUrl;
  }

  /**
   *
   * Register a callback to get notified about auth state changes
   *
   * ```typescript
   *
   *  import authMgr from '@ion/applications/src/authentication/AuthenticationManager';
   *
   *  useEffect(() => {
   *    authMgr.registerCallback(someFn);
   *    return () => authMgr.unregisterCallback(someFn);
   *  }, []);
   *
   * ```
   *
   */
  public registerCallback(cb: Function): void {
    if (cb) {
      this._callbacks.push(cb);
      setTimeout(() => cb(this.authState), 0);
    }
  }

  /**
   *
   * Unregister a callback to get notified about auth state changes
   *
   * ```typescript
   *
   *  import authMgr from '@ion/applications/src/authentication/AuthenticationManager';
   *
   *  useEffect(() => {
   *    authMgr.registerCallback(someFn);
   *    return () => authMgr.unregisterCallback(someFn);
   *  }, []);
   *
   * ```
   *
   */
  public unregisterCallback(cb: Function): void {
    if (cb) {
      const index = this._callbacks.indexOf(cb);
      if (index > -1) {
        this._callbacks.splice(index, 1);
      }
    }
  }

  /**
   * Retrieve persisted user details from local storage
   */
  private getPersisted() {
    const item = safeJSONParse(
      JSON.stringify(this.loginPersistence(LOGIN_PERSISTENCE.LOGIN_CREDENTIALS) || this._emptyCredentials)
    );
    const {password} = item?.credentials;
    if (password) {
      try {
        item.credentials.password = decode(password);
      } catch (err) {
        error(err.toString());
      }
    }
    return item;
  };

  /**
   * Util to store/retrieve user details to local storage
   */
  private loginPersistence(key: LOGIN_PERSISTENCE, remove: boolean = false, value?: any): any {
    if (Object.values(LOGIN_PERSISTENCE).includes(key)) {
      const store = getLocalStorage(ApplicationKeys.iOnSuite) || {};
      if (remove) {
        delete store[key];
      } else if (isNil(value)) {
        return store[key];
      } else {
        store[key] = value;
      }
      setLocalStorage(ApplicationKeys.iOnSuite, store);
    }
  }

  /**
   * Store user details to local storage
   */
  private persistUser(persist: boolean, username: string, password: string): void {
    if (persist) {
      this._user = {username, password: password ? encode(password) : undefined};
      this._persistCredentials = true;
      this._persisted = {
        credentials : this._user,
        rememberMe  : this._persistCredentials
      };
      this.loginPersistence(LOGIN_PERSISTENCE.LOGIN_CREDENTIALS, false, this._persisted);
    } else {
      this.loginPersistence(LOGIN_PERSISTENCE.LOGIN_CREDENTIALS, true);
    }
    this.loginPersistence(LOGIN_PERSISTENCE.LOGIN_SESSION, false, new Date().getTime());
  }

  /**
   *
   * Getter to retrieve rememberMe settings
   *
   * ```typescript
   *
   *  import authMgr from '@ion/applications/src/authentication/AuthenticationManager';
   *
   *  rememberMe = authMgr.persistCredentials;
   *
   * ```
   *
   */
  public get persistCredentials(): boolean {
    return this._persistCredentials;
  }

  /**
   *
   * Setter to store rememberMe settings
   *
   * ```typescript
   *
   *  import authMgr from '@ion/applications/src/authentication/AuthenticationManager';
   *
   *  authMgr.persistCredentials = rememberMe;
   *
   * ```
   *
   */
  public set persistCredentials(persist: boolean) {
    this._persistCredentials = persist;
  }

  /**
   *
   * Getter to retrieve persisted user
   *
   * ```typescript
   *
   *  import authMgr from '@ion/applications/src/authentication/AuthenticationManager';
   *
   *  user = authMgr.persistedUser;
   *
   * ```
   *
   */
  public get persistedUser(): GenericObject {
    return this._user;
  }

  /**
   *
   * Getter to retrieve oidcUsername
   *
   * ```typescript
   *
   *  import authMgr from '@ion/applications/src/authentication/AuthenticationManager';
   *
   *  oidcUsername = authMgr.oidcUsername;
   *
   * ```
   *
   */
  public get oidcUsername(): string {
    if (!this._oidcUsername) {
      this._oidcUsername = this.loginPersistence(LOGIN_PERSISTENCE.OIDC_USERNAME) || '';
    }
    return this._oidcUsername;
  }

  /**
   *
   * Setter to store oidcUsername
   */
  private set oidcUsername(oidcUsername: string) {
    this._oidcUsername = oidcUsername;
    this.loginPersistence(LOGIN_PERSISTENCE.OIDC_USERNAME, false, oidcUsername);
  }

  /**
   *
   * Util to delete temporary oidcUsername
   */
  private deleteOidcUsername(): void {
    this._oidcUsername = null;
    this.loginPersistence(LOGIN_PERSISTENCE.OIDC_USERNAME, true);
  }

  /**
   *
   * Getter to retrieve hasIdProvider
   *
   * ```typescript
   *
   *  import authMgr from '@ion/applications/src/authentication/AuthenticationManager';
   *
   *  oidcUsername = authMgr.hasIdProvider;
   *
   * ```
   *
   */
  public get hasIdProvider(): boolean {
    return this._hasIdProvider;
  }

  /**
   *
   * Util to set Auth State
   */
  private set authState(authState: OIDCAuthState) {
    this._authState = authState;
    this._callbacks.forEach((cb) => cb(authState));
  }

  /**
   *
   * Util to get Auth State
   */
  private get authState(): OIDCAuthState {
    return this._authState;
  }

  /**
   *
   * Getter to retrieve isInvalid
   *
   * ```typescript
   *
   *  import authMgr from '@ion/applications/src/authentication/AuthenticationManager';
   *
   *  isInvalid = authMgr.isInvalid;
   *
   * ```
   *
   */
  public get isInvalid(): boolean {
    return this._isInvalid;
  }

  /**
   * Provides a utility which determines if the given auth state matches the specified auth
   * state.
   */
  private authStateStatus(state: OIDCAuthState, isEqualTo: boolean = true): boolean {
    return isEqualTo ? this._authState === state : this._authState !== state;
  }

  /**
   * Util to set discovery start state
   */
  private oidcDiscoveryStart(): void {
    this._isServiceError = false;
    this._isInvalid = false;
    this.authState = OIDCAuthState.OIDC_DISCOVERY_START;
  }

  /**
   * Util to set discovery fail state
   */
  private oidcDiscoveryFail(): void {
    this.authState = OIDCAuthState.OIDC_DISCOVERY_FAILURE;
    this._hasIdProvider = false;
  }

  /**
   *
   * Util to set discovery status successful
   *
   * ```typescript
   *
   *  import authMgr from '@ion/applications/src/authentication/AuthenticationManager';
   *
   *  authMgr.oidcDiscoverySuccess(oiidcUsername);
   *
   * ```
   *
   */
  public oidcDiscoverySuccess(oidcUsername: string): void {
    this.authState = OIDCAuthState.OIDC_DISCOVERY_SUCCESS;
    this.oidcUsername = oidcUsername;
    this._hasIdProvider = true;
  }

  /**
   *
   * Util to start OIDC discovery routine
   *
   * ```typescript
   *
   *  import authMgr from '@ion/applications/src/authentication/AuthenticationManager';
   *
   *  authMgr.oidcDiscovery(oidcUsername);
   *
   * ```
   *
   */
  public async oidcDiscovery(username): Promise<boolean> {
    let isIdpUser = false;
    try {
      this.oidcDiscoveryStart();
      const {isSso} = await this.getOidcConfig(username);
      isIdpUser = isSso;
      if (isIdpUser) {
        this.oidcDiscoverySuccess(username);
      } else {
        this.oidcDiscoveryFail();
      }
    } catch (err) {
      error(err);
      this.oidcDiscoveryFail();
    }
    return isIdpUser;
  }

  /**
   *
   * Utility which determines if auth state is OIDCAuthState.OIDC_DISCOVERY_FAILURE
   *
   * ```typescript
   *
   *  import authMgr from '@ion/applications/src/authentication/AuthenticationManager';
   *
   *  authMgr.isOidcDiscoveryFailure();
   *
   * ```
   *
   */
  public isOidcDiscoveryFailure(): boolean {
    return this.authStateStatus(OIDCAuthState.OIDC_DISCOVERY_FAILURE);
  }

  /**
   *
   * Utility which determines if auth state is OIDCAuthState.OIDC_AUTH_DEFAULT
   *
   * ```typescript
   *
   *  import authMgr from '@ion/applications/src/authentication/AuthenticationManager';
   *
   *  authMgr.isOidcDiscoveryStart();
   *
   * ```
   *
   */
  public isOidcDiscoveryStart(): boolean {
    return this.authStateStatus(OIDCAuthState.OIDC_AUTH_DEFAULT);
  }

  /**
   *
   * Utility to set auth state to OIDCAuthState.LOGIN_START
   *
   * ```typescript
   *
   *  import authMgr from '@ion/applications/src/authentication/AuthenticationManager';
   *
   *  authMgr.loginStart(username);
   *
   * ```
   *
   */
  public loginStart(oidcUsername: string): void {
    this.authState = OIDCAuthState.LOGIN_START;
    this.oidcUsername = oidcUsername;
  }

  /**
   *
   * Utility to set auth state to OIDCAuthState.LOGIN_SUCCESS
   *
   * ```typescript
   *
   *  import authMgr from '@ion/applications/src/authentication/AuthenticationManager';
   *
   *  authMgr.loginSuccess(username);
   *
   * ```
   *
   */
  public loginSuccess(oidcUsername: string): void {
    this.authState = OIDCAuthState.LOGIN_SUCCESS;
    this.loginPersistence(LOGIN_PERSISTENCE.USER_NAME_VERIFICATION_KEY, false, oidcUsername);
    this._isServiceError = false;
    this._isInvalid = false;
    this.deleteOidcUsername();
  }

  /**
   *
   * Utility to set auth state to OIDCAuthState.LOGIN_FAILURE
   *
   * ```typescript
   *
   *  import authMgr from '@ion/applications/src/authentication/AuthenticationManager';
   *
   *  authMgr.loginFail();
   *
   * ```
   *
   */
  public loginFail(permissionError: boolean = false): void {
    this._isServiceError = true;
    this._isInvalid = !permissionError;
    this._authClient?.closeSession();
    this.authState = OIDCAuthState.LOGIN_FAILURE;
    this._authClient = null;
    this.deleteOidcUsername();
  }

  /**
   *
   * Utility to determine if app is authenticating
   *
   * ```typescript
   *
   *  import authMgr from '@ion/applications/src/authentication/AuthenticationManager';
   *
   *  authMgr.isAuthenticating();
   *
   * ```
   *
   */
  public isAuthenticating(): boolean {
    return (this.authStateStatus(OIDCAuthState.LOGIN_START) ||
      this.authStateStatus(OIDCAuthState.OIDC_DISCOVERY_START));
  }

  /**
   *
   * Utility to determine if app login failed
   *
   * ```typescript
   *
   *  import authMgr from '@ion/applications/src/authentication/AuthenticationManager';
   *
   *  authMgr.isLoginFailure();
   *
   * ```
   *
   */
  public isLoginFailure(): boolean {
    return this.authStateStatus(OIDCAuthState.LOGIN_FAILURE);
  }

  /**
   *
   * Provides a pre-authentication check which first attempts to retrieve the current
   * user if the user has an session timestamp persisted that is within the same day
   * (CTC timeouts are based on a one hour of no activity interval; however, we retry
   * for the duration of a day as the session may continue to be renewed). If returned
   * successfully confirms that the users current session is still active, otherwise,
   * if the call fails, returns null and the user is redirected to the login page.
   *
   * ```typescript
   *
   *  import authMgr from '@ion/applications/src/authentication/AuthenticationManager';
   *
   *  authMgr.preauth(isFromPrime)
   *
   * ```
   *
   * @param  isFromPrime When true, determines if the users session is current active
   *         and if so, retrieves the current user.
   * @param  useApiTierUser When true, indicates that API-Tier GraphQL Services are
   *         to be used when retrieving the current user, otherwise, when `false`
   *         (default), indicates the the current User is to be retrieved via CTC
   *         Services.
   */
  public async preauth(isFromPrime: boolean, useApiTierUser = false): Promise<User> {
    const session = this.loginPersistence(LOGIN_PERSISTENCE.LOGIN_SESSION);
    try {
      const username = this.loginPersistence(LOGIN_PERSISTENCE.USER_NAME_VERIFICATION_KEY);
      const isIdpUser = username ? await this.oidcDiscovery(username) : false;
      const api = !useApiTierUser ? currentUser : currentUserQuery;
      if (isIdpUser) {
        const accessToken = await this.getAccessToken(username);
        if (accessToken) {
          await this.updateHeaders(accessToken);
          if (useApiTierUser) {
            setGraphQLAuthToken(accessToken.accessToken);
          }
          return await api();
        }
      } else {
        if (isFromPrime || (session && isSame(session, new Date(), 'day'))) {
          if (useApiTierUser) {
            const token = await this.getToken();
            setGraphQLAuthToken(token);
          }
          return await api();
        }
      }
    } catch (err) {
    }
    return null;
  }

  /**
   *
   * CTC authentication routine. Returns the user entity on successful login
   *
   * ```typescript
   *
   *  import authMgr from '@ion/applications/src/authentication/AuthenticationManager';
   *
   *  authMgr.ctcLogin(username, password)
   *
   * ```
   *
   * @param  username The username which is to be authenticated with CTC Services.
   * @param  password the password which is to be authenticated with CTC Services.
   * @param  useCTC When true (default), the user will be authenticated with CTC
   *         Services, otherwise, the user will be authenticated with SD Services.
   * @param  useApiTierUser When true, indicates that API-Tier GraphQL Services are
   *         to be used when retrieving the current user, otherwise, when `false`
   *         (default), indicates the the current User is to be retrieved via CTC
   *         Services.
   */
  // eslint-disable-next-line max-params
  public async ctcLogin(username, password, useCTC = true, useApiTierUser = false): Promise<User> {
    let user = null;
    try {
      this.loginStart(username);
      this.persistUser(this.persistCredentials, username, password);
      const api = !useApiTierUser ? currentUser : currentUserQuery;
      useCTC ? await authenticate(username, password) : await sdAuthenticate(username, password);
      if (useApiTierUser) {
        const token = await this.getToken();
        setGraphQLAuthToken(token);
      }
      user = await api();
      this.loginSuccess(username);
    } catch (e) {
      this.loginFail();
    }
    return user;
  }

  /**
   *
   * OIDC authentication routine. Redirects app to OIDC login page
   *
   * ```typescript
   *
   *  import authMgr from '@ion/applications/src/authentication/AuthenticationManager';
   *
   *  authMgr.oidcLogin(username)
   *
   * ```
   *
   */
  public oidcLogin(username): void {
    this.persistUser(this.persistCredentials, username, null);
    this.oidcRedirect(username);
  }

  /**
   *
   * Clean up session related persistent info
   *
   * ```typescript
   *
   *  import authMgr from '@ion/applications/src/authentication/AuthenticationManager';
   *
   *  authMgr.signoutCleanup()
   *
   * ```
   *
   */
  public signoutCleanup() {
    this.loginPersistence(LOGIN_PERSISTENCE.USER_NAME_VERIFICATION_KEY, true);
    this.loginPersistence(LOGIN_PERSISTENCE.LOGIN_SESSION, true);
    this.deleteOidcUsername();
  }

  /**
   *
   * Return the logged in user's username
   *
   * ```typescript
   *
   *  import authMgr from '@ion/applications/src/authentication/AuthenticationManager';
   *
   *  authMgr.getLoggedInUsername()
   *
   * ```
   *
   */
  public getLoggedInUsername(): string {
    return this.loginPersistence(LOGIN_PERSISTENCE.USER_NAME_VERIFICATION_KEY);
  }

  /**
   *
   * Utility to reset auth state to OIDCAuthState.OIDC_AUTH_DEFAULT
   *
   * ```typescript
   *
   *  import authMgr from '@ion/applications/src/authentication/AuthenticationManager';
   *
   *  authMgr.resetAuthState();
   *
   * ```
   *
   */
  public resetAuthState(): void {
    this.authState = OIDCAuthState.OIDC_AUTH_DEFAULT;
  }

  /**
   *
   * Utility to retrieve open id configuration from platform
   *
   * ```typescript
   *
   *  import authMgr from '@ion/applications/src/authentication/AuthenticationManager';
   *
   *  authMgr.getOidcConfig(username, accountId);
   *
   * ```
   */
  public async getOidcConfig(usernameWithDomain?: string, accountId?: string): Promise<SSOConfigResponse> {
    const ssoResponse: SSOConfigResponse = {
      error  : false,
      isSso  : false,
      config : {}
    };
    try {
      const provider = await queryOpenIdConfigurationForDomain(this.oidcBaseUrl, usernameWithDomain, accountId);
      const {issuer = null, client_id: clientId = null, is_default = true} = provider || {};
      if (!isNil(issuer) && !isNil(clientId) && is_default === false) {
        const includeArgs = get('_oidcEnabled') === false && this.getIsOIDCEnabled() === true;
        const redirectUri = includeArgs ? `${window.location.origin}/login${window?.location?.search}` : `${window.location.origin}/login`;
        ssoResponse.isSso = true;
        ssoResponse.config = {
          issuer,
          clientId,
          redirectUri,
          responseMode : 'fragment',
          scopes       : ['openid', 'email', 'offline_access'],
          tokenManager : {
            storage : 'cookie'
          }
        };
      }
    } catch (err) {
      error(err);
      ssoResponse.error = true;
      const {response} = err || {};
      const {status} = response || {};
      if (this.isStrictSSOError(status) && accountId) {
        ssoResponse.isSso = true;
      }
    }
    return ssoResponse;
  }

  /**
   * The getValidDomainFromUsername utility extracts email domain from username
   *
   */
  private getValidDomainFromUsername(username: string) {
    const domain = username ? username.includes('@') && username.split('@').length === 2 ? username.split('@').pop() : null : null;
    if (domain?.split('.').length <= 1) {
      return null;
    }
    return domain;
  }

  /**
   * Utility to return okta auth client instance after retrieving open id
   * configuration from platform
   *
   */
  private async getAuthClient(username?: string): Promise<OktaAuth> {
    if (!this._authClient) {
      const domain = this.getValidDomainFromUsername(username);
      if (domain) {
        const {config, error, isSso} = await this.getOidcConfig(username);
        if (!error && !isEmpty(config) && isSso) {
          this._authClient = new OktaAuth(config);
        }
      }
    }
    return this._authClient;
  }

  /**
   *
   * Utility to check user info using the access token
   */
  private async getUserInfo(accessToken: AccessToken) {
    const {tokenType, accessToken:token, userinfoUrl} = accessToken;
    const authorization = `${tokenType} ${token}`;
    const userInfo = await getOpenIdUserInfo(userinfoUrl, authorization);
    return userInfo;
  }

  /**
   * Entry point function to parse out access token from auth code returned
   * by OIDP
   *
   * ```typescript
   *
   *  import authMgr from '@ion/applications/src/authentication/AuthenticationManager';
   *
   *  authMgr.initializeAuthentication(username);
   *
   * ```
   */
  public async initializeAuthentication(username: string): Promise<any> {
    const result = {
      initialized : false,
      state       : null
    };
    try {
      const authClient: OktaAuth = await this.getAuthClient(username);
      const {tokens, state} = await authClient?.token.parseFromUrl();
      result.state = state;
      const {accessToken, idToken, refreshToken} = tokens;
      idToken && authClient.tokenManager.add('idToken', idToken);
      accessToken && authClient.tokenManager.add('accessToken', accessToken);
      refreshToken && authClient.tokenManager.add('refreshToken', refreshToken);
      const userInfo: UserClaims = await authClient.getUser();
      if (!userInfo) {
        throw ('Invalid token');
      }
      const user = await this.getUserInfo(accessToken);
      if (user?.email && user?.email.toLowerCase() === username.toLowerCase()) {
        await this.updateHeaders(accessToken);
        result.initialized = true;
      }
    } catch (err) {
      error(err);
    }
    return result;
  }

  /**
   * Util function to return access token
   *
   * ```typescript
   *
   *  import authMgr from '@ion/applications/src/authentication/AuthenticationManager';
   *
   *  authMgr.getAccessToken();
   *
   * ```
   * @returns Access Token
   */
  public async getAccessToken(username?: string): Promise<AccessToken> {
    const authClient: OktaAuth = await this.getAuthClient(username);
    return await authClient?.tokenManager.get('accessToken');
  };

  /**
   * Provides an API which allows for referencing the JWT (accessToken) for the authenticated
   * User. This method is intended to be used by clients who need to provide the authorization
   * token in their request headers.
   *
   * ```typescript
   *   import AuthenticationManager from '@ion/applications/src/authentication';
   *
   *   const token = await AuthenticationManager.getToken();
   *   // set client Authorization header ...
   * ```
   *
   * @param  An optional username against which to initialize the authorization client.
   * @returns A Promise containing the authorization (JWT) token for the given session.
   */
  public async getToken(username?: string): Promise<string> {
    try {
      const {accessToken} = await this.getAccessToken(username);
      return accessToken;
    } catch (err) {
      return Promise.resolve(getSessionToken());
    }
  }

  /**
   * Provides an API which allows for referencing the JWT (accessToken) expiration
   * for the authenticated User. This method is intended to be used by clients who
   * need to perform certain checks based on the expiry of the token.
   *
   * ```typescript
   *   import AuthenticationManager from '@ion/applications/src/authentication';
   *
   *   const expiration = await AuthenticationManager.getExpiration();
   *   ...
   * ```
   *
   * @param  An optional username against which to initialize the authorization
   *         client.
   * @returns A Promise containing the authorization (JWT) token expiration for
   *          the given session.
   */
  public async getExpiration(username?: string): Promise<number | string> {
    try {
      const client: OktaAuth = await this.getAuthClient(username);
      const {accessToken} = await this.getAccessToken(username);
      return client.tokenManager.getExpireTime(accessToken);
    } catch (err) {
      return Promise.resolve(getSessionTokenExpiration());
    }
  }

  /**
   * Util function to initiate auth by redirecting to oidp portal
   *
   * @param username the user's username.
   */
  private async oidcRedirect(username) {
    const authClient: OktaAuth = await this.getAuthClient(username);
    if (!authClient) {
      return null;
    }
    return authClient.token.getWithRedirect({
      loginHint : username,
      state     : username,
      pkce      : true
    }).catch((err) => {
      this.loginFail();
      error(err);
    });
  }

  /**
   * Util function to renew access token
   *
   * ```typescript
   *
   *  import authMgr from '@ion/applications/src/authentication/AuthenticationManager';
   *
   *  authMgr.renewToken();
   *
   * ```
   */
  public async renewToken(username?: string) {
    const authClient: OktaAuth = await this.getAuthClient(username);
    const newToken = await authClient?.tokenManager.renew('accessToken');
    await this.updateHeaders(newToken as AccessToken);
  }

  /**
   * Util function for oidc logout redirect
   *
   * ```typescript
   *
   *  import authMgr from '@ion/applications/src/authentication/AuthenticationManager';
   *
   *  authMgr.oidcLogout();
   *
   * ```
   */
  public async oidcLogout(username?: string) {
    const authClient = await this.getAuthClient(username);
    try {
      logout();
    } catch (e) {}
    const includeArgs = get('_oidcEnabled') === false && this.getIsOIDCEnabled() === true;
    const postLogoutRedirectUri = includeArgs ? `${window.location.origin}${window?.location?.search}` : window.location.origin;
    await authClient?.signOut({
      postLogoutRedirectUri
    });
  }

  /**
   * The registerAccessToken method registers the OIDC access token with CTC,
   * which sets it back as cookie in the browser.
   *
   */
  private async registerAccessToken(accessToken: AccessToken): Promise<any> {
    const {tokenType, accessToken:token} = accessToken;
    const authorization = `${tokenType} ${token}`;
    const result = await client({
      url     : url('/api/token', Service.Base),
      method  : 'post',
      headers : {
        authorization
      }
    });
    return result?.data;
  }

  /**
   *
   * Utility to update authorization headers in various Axios clients
   *
   * ```typescript
   *
   *  import authMgr from '@ion/applications/src/authentication/AuthenticationManager';
   *
   *  authMgr.updateHeaders(accessToken);
   *
   * ```
   */
  public async updateHeaders(accessToken: AccessToken): Promise<boolean> {
    if (!accessToken) return false;
    await this.registerAccessToken(accessToken);

    //TODO - In future, set authorization header for individual clients when those services are ready to
    // accept Authorization headers.
    // const {tokenType, accessToken:token} = accessToken;
    // const authHeader = `${tokenType} ${token}`;
    // setConnectAuthorizationHeader(authHeader);
    // setArcGISAuthorizationHeader(authHeader);
    // setSDConnectAuthorizationHeader(authHeader);
    return true;
  }

  /**
   *
   * Utility to set auth state to OIDCAuthState.PREAUTH_FAILURE
   *
   * ```typescript
   *
   *  import authMgr from '@ion/applications/src/authentication/AuthenticationManager';
   *
   *  authMgr.preauthFail();
   *
   * ```
   *
   */
  public preauthFail(): void {
    this._isInvalid = false;
    this._isServiceError = true;
    this.authState = OIDCAuthState.PREAUTH_FAILURE;
    this.deleteOidcUsername();
  }

  /**
   *
   * Utility to determine if app preuth failed
   *
   * ```typescript
   *
   *  import authMgr from '@ion/applications/src/authentication/AuthenticationManager';
   *
   *  authMgr.isPreauthFailure();
   *
   * ```
   *
   */
  public isPreauthFailure(): boolean {
    return this.authStateStatus(OIDCAuthState.PREAUTH_FAILURE);
  }

  /**
   *
   * Utility to determine if error code is strict sso http error code
   *
   * ```typescript
   *
   *  import authMgr from '@ion/applications/src/authentication/AuthenticationManager';
   *
   *  authMgr.isStrictSSOError();
   *
   * ```
   *
   */
  private isStrictSSOError(statusCode: number): boolean {
    const codes = Object.values(SSO_HTTP_ERROR);
    return codes.includes(statusCode);
  }
};

export default new AuthenticationManager();
